// A car's page: the car read once, its name in the back link and the browser's title ("R34: Settings"), and its pages'
// scope bar on a desktop. A car that's gone says so; one still loading shows its shape.
import type { components } from '@tune/shared';
import type { ReactNode } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../../api';
import { describeCar } from '../../lib/car';
import { useVehicle } from '../../queries';
import { Page, type Back } from '../../shell/Page';
import { ButtonLink, EmptyState, ErrorBanner, Skeleton } from '../../ui';
import { CarIcon } from '../../ui/icons';
import { CarScopes } from '../garage/CarNav';

type Vehicle = components['schemas']['Vehicle'];

const GARAGE: Back = { to: '/garage', label: 'Garage' };

export function CarPage({
  section,
  back,
  actions,
  headActions,
  children,
}: {
  /** The page's name: "Settings". None for the car's own page, its overview. */
  section?: string;
  /** Where Back goes, when not the car's overview: Settings, for its history. */
  back?: (car: Vehicle) => Back;
  actions?: ReactNode;
  /** Beside the large title: the page's main action, like "Log a service". */
  headActions?: ReactNode;
  children: (car: Vehicle) => ReactNode;
}) {
  const { vehicleId = '' } = useParams();
  const vehicle = useVehicle(vehicleId);
  const car = vehicle.data;
  if (car) {
    return (
      <Page
        title={section ?? car.nickname}
        tab={section ? `${car.nickname}: ${section}` : car.nickname}
        subtitle={section ? undefined : describeCar(car) || undefined}
        back={back?.(car) ?? (section ? { to: `/garage/${car.id}`, label: car.nickname } : GARAGE)}
        actions={actions}
        headActions={headActions}
      >
        <CarScopes car={car} />
        {children(car)}
      </Page>
    );
  }
  if (vehicle.isError && vehicle.error instanceof ApiError && vehicle.error.status === 404) {
    return (
      <Page title="No such car" back={GARAGE}>
        <EmptyState
          icon={<CarIcon size={56} />}
          title="This car isn't in your garage"
          action={<ButtonLink to="/garage">Go to your garage</ButtonLink>}
        >
          It may have been deleted.
        </EmptyState>
      </Page>
    );
  }
  return (
    <Page title={section ?? 'Car'} back={GARAGE}>
      {vehicle.isError ? (
        <ErrorBanner retry={() => void vehicle.refetch()}>The car couldn't load.</ErrorBanner>
      ) : (
        <Skeleton blocks={3} lines={2} label="Loading the car" />
      )}
    </Page>
  );
}
